import {
  Attachment as DefaultAttachment,
  type AttachmentProps,
  useMessageContext,
} from "stream-chat-react";
import { PaymentRequestCard } from "Components/Chat/PaymentRequestCard";
import { DirectPaymentCard } from "Components/Chat/DirectPaymentAttachment";
import { DirectPaymentCompactRow } from "Components/Chat/DirectPaymentCompactRow";
import { MultiPayerPaymentRequestCard } from "Components/Chat/MultiPayerPaymentRequestCard";
import {
  isCustomAttachment,
  type PaymentRequestAttachment as PaymentRequestAttachmentType,
  type MultiPayerPaymentRequestAttachment as MultiPayerPaymentRequestAttachmentType,
  type DirectPaymentAttachment as DirectPaymentAttachmentType,
  type CustomAttachmentType,
} from "Components/Chat/chatAttachmentTypes";

const isPaymentRequest = (a: unknown): a is PaymentRequestAttachmentType =>
  typeof a === "object" &&
  a !== null &&
  "type" in a &&
  (a as { type: CustomAttachmentType }).type === "payment_request" &&
  "transactionPublicID" in a;

const isMultiPayerPaymentRequest = (
  a: unknown,
): a is MultiPayerPaymentRequestAttachmentType =>
  typeof a === "object" &&
  a !== null &&
  "type" in a &&
  (a as { type: CustomAttachmentType }).type === "payment_request_multi" &&
  "recipientIdentityUserPublicID" in a;

const isDirectPayment = (a: unknown): a is DirectPaymentAttachmentType =>
  typeof a === "object" &&
  a !== null &&
  "type" in a &&
  (a as { type: CustomAttachmentType }).type === "direct_payment" &&
  "transactionPublicID" in a;

export const ChatAttachmentRouter = ({
  attachments,
  isQuoted,
  ...rest
}: AttachmentProps) => {
  const { message } = useMessageContext();

  // A quoted message renders its attachments outside its own message context: the composer's
  // reply preview has no MessageContext at all (`message` is undefined), and the quote block of a
  // sent reply sits in the REPLYING message's context. The payment cards read the message they
  // belong to from that context, so a quoted payment keeps only its caption (the push/preview text)
  // and the built-in attachments.
  if (isQuoted) {
    const quotedAttachments = attachments.filter((a) => !isCustomAttachment(a));

    if (quotedAttachments.length === 0) {
      return null;
    }

    return (
      <DefaultAttachment {...rest} isQuoted attachments={quotedAttachments} />
    );
  }

  // A payment posted as a thread reply (under a multi-payer request) is one of many and gets the
  // one-line row; the same payment in the conversation keeps the full card.
  const DirectPayment = message.parent_id
    ? DirectPaymentCompactRow
    : DirectPaymentCard;

  const paymentRequestAttachments = attachments.filter(isPaymentRequest);
  const multiPayerRequestAttachments = attachments.filter(
    isMultiPayerPaymentRequest,
  );
  const directPaymentAttachments = attachments.filter(isDirectPayment);
  const otherAttachments = attachments.filter((a) => !isCustomAttachment(a));

  return (
    <>
      {paymentRequestAttachments.map((attachment) => (
        <PaymentRequestCard
          key={attachment.transactionPublicID}
          attachment={attachment}
        />
      ))}
      {multiPayerRequestAttachments.map((attachment) => (
        <MultiPayerPaymentRequestCard
          // The author is who gets paid; the attachment's recipient id is only a claim.
          key={message.user?.id ?? message.id}
          attachment={attachment}
        />
      ))}
      {directPaymentAttachments.map((attachment) => (
        <DirectPayment
          key={attachment.transactionPublicID}
          attachment={attachment}
        />
      ))}
      {otherAttachments.length > 0 && (
        <DefaultAttachment {...rest} attachments={otherAttachments} />
      )}
    </>
  );
};

// Backward-compatible export for Stream Chat's Attachment prop
export const PaymentRequestAttachment = ChatAttachmentRouter;
